import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import ArrowButton from "../ArrowButton/ArrowButton";
import "./CtaBand.css";

// medidas no sistema do viewBox (1440 x 260)
const WIDTH = 1440;
const MID = 130;
const STEP = 12;
const SPEED = 280; // velocidade com que a trança corre para a direita (unid./s)
const TWIST = 820; // uma volta da trança: dois cruzamentos
const START = 1.2; // instante do desenho parado (reduced motion)
const ROPES = [1, -1]; // lado de cada corda em relação à linha central

// pontos rosa que deslizam presos às cordas
const DOTS = [
  { rope: 0, x: 300, r: 3.5, speed: 140 },
  { rope: 0, x: 760, r: 4, speed: 110 },
  { rope: 0, x: 1080, r: 3, speed: 170 },
  { rope: 1, x: 1240, r: 4, speed: 125 },
  { rope: 1, x: 560, r: 2.5, speed: 155 },
];

// as duas cordas espelham uma à outra em volta de uma linha central: sempre cruzadas, a intervalos iguais
function ropeY(rope: number, x: number, time: number) {
  const u = x - SPEED * time; // posição presa à trança, que anda para a direita
  const center = MID + 55 * Math.sin((u / 2200) * Math.PI * 2 + 0.3) + 24 * Math.sin(time * 0.6);
  // barrigas de tamanhos diferentes entre si, mas cada uma mantém o seu enquanto anda
  const size = 54 + 30 * Math.sin((u / 2900) * Math.PI * 2 + 0.7) + 8 * Math.sin(time * 0.8);
  return center + ROPES[rope] * size * Math.sin((u / TWIST) * Math.PI * 2);
}

function ropePath(rope: number, time: number) {
  let d = "";
  for (let x = -20; x < WIDTH + 20 + STEP; x += STEP) {
    d += `${d ? "L" : "M"}${x} ${ropeY(rope, x, time).toFixed(1)}`;
  }
  return d;
}

// sai pela direita e volta pela esquerda, sempre fora da área visível
function dotX(dot: (typeof DOTS)[number], time: number) {
  const span = WIDTH + 80;
  return ((((dot.x + 40 + dot.speed * time) % span) + span) % span) - 40;
}

export default function CtaBand() {
  const { t } = useTranslation();
  const artRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = artRef.current;
    if (!svg || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ropes = svg.querySelectorAll("path");
    const dots = svg.querySelectorAll("circle");
    let time = START;
    let last = 0;
    let raf = 0;

    const tick = (now: number) => {
      // passo limitado: ao voltar de uma aba escondida a animação continua de onde parou
      time += last ? Math.min((now - last) / 1000, 1 / 30) : 0;
      last = now;
      ropes.forEach((el, i) => el.setAttribute("d", ropePath(i, time)));
      dots.forEach((el, i) => {
        const x = dotX(DOTS[i], time);
        el.setAttribute("cx", x.toFixed(1));
        el.setAttribute("cy", ropeY(DOTS[i].rope, x, time).toFixed(1));
      });
      raf = requestAnimationFrame(tick);
    };

    // só anima com a faixa na tela
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!raf) raf = requestAnimationFrame(tick);
      } else {
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      }
    });
    io.observe(svg);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="cz-cta-band" aria-labelledby="cz-cta-band-title">
      {/* cordas e pontos rosa decorativos, animados no useEffect */}
      <svg ref={artRef} className="cz-cta-band__art" viewBox="0 0 1440 260" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        {ROPES.map((_, i) => (
          <path key={i} d={ropePath(i, START)} />
        ))}
        {DOTS.map((dot, i) => {
          const x = dotX(dot, START);
          return <circle key={i} cx={x.toFixed(1)} cy={ropeY(dot.rope, x, START).toFixed(1)} r={dot.r} />;
        })}
      </svg>
      <div className="cz-cta-band__inner">
        <div className="cz-cta-band__text">
          <span className="cz-cta-band__bar" aria-hidden="true" />
          <h2 className="cz-cta-band__title" id="cz-cta-band-title">
            {t("home.cta_title", "Pronto para transformar desafios em resultados?")}
          </h2>
          <p className="cz-cta-band__desc">
            {t("home.cta_text", "Converse com nossos especialistas e descubra como a Calriz pode impulsionar sua instituição com tecnologia, segurança e impacto real.")}
          </p>
        </div>
        <div className="cz-cta-band__actions">
          <ArrowButton to="#contato" variant="pink" size="md">
            {t("home.hero_cta", "Fale com um especialista")}
          </ArrowButton>
          <Link to="/solucoes" className="cz-outline-btn cz-outline-btn--on-dark cz-cta-band__secondary">
            {t("home.hero_cta_secondary", "Conheça nossas soluções")}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
